import { QueryObserverResult } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useCallback, useEffect, useRef } from "react";
import { LoginConfigResponse } from "../types/loginConfig";
import { buildOktaAuthUrl } from "../utils/api.utils";

interface UseOktaLoginConfig {
  fetchLoginConfig: () => Promise<
    QueryObserverResult<LoginConfigResponse, AxiosError>
  >;
  onSuccess: (code: string, state: string) => void;
  onError: (err: unknown) => void;
  width?: number;
  height?: number;
  pollInterval?: number;
}

export function useOktaLogin({
  fetchLoginConfig,
  onSuccess,
  onError,
  width = 500,
  height = 700,
}: UseOktaLoginConfig) {
  const popupRef = useRef<Window | null>(null);

  const openOktaPopup = useCallback(async () => {
    try {
      // Fetch the login config
      const result = await fetchLoginConfig();
      if (!result.data) throw new Error("Login config not found");
      const authUrl = buildOktaAuthUrl(result.data);

      // Open the popup with the auth URL
      const popup = window.open(
        authUrl,
        "oktaPopup",
        `width=${width},height=${height},scrollbars=yes`
      );
      if (!popup) throw new Error("Could not open popup");
      popupRef.current = popup;
    } catch (err) {
      console.error("Okta popup error", err);
      onError(err);
    }
  }, [fetchLoginConfig, onError, width, height]);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      const { type, code, state } = event.data as any;
      if (type !== "oktaCallback") return;

      onSuccess(code, state);
      popupRef.current?.close();
    }

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [onSuccess]);

  return openOktaPopup;
}
